// Consignaciones (pedido de Rodrigo 2026-10-23): mercadería que se deja donde el cliente, puede volver y
// se factura solo lo consumido. La consignación es una ubicación de la empresa en el local del cliente
// (la mercadería sigue siendo de MADECI): enviar y recibir lo que vuelve son traslados, cada uno con su
// guía interna (GDI, opción A); lo consumido se factura con una factura directa desde esa ubicación.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { useUbicaciones } from '../api/consultas';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorCliente } from './BuscadorCliente';
import { BuscadorProducto } from './BuscadorProducto';
import { GuiaInternaImpresa } from './GuiaInterna';

type Stock = {
  productoId: string;
  sku: string;
  nombre: string;
  unidad: string;
  fisico: string;
  reservado: string;
  disponible: string;
};
type Consignacion = {
  ubicacionId: string;
  codigo: string;
  nombre: string;
  activa: boolean;
  cliente: { id: string; razonSocial: string; rut: string | null };
  stock: Stock[];
};
type Linea = {
  productoId: string;
  sku: string;
  nombre: string;
  unidad: string;
  cantidad: string;
  tope?: number;
};

const cant = (s: string) => Number(s).toLocaleString('es-CL', { maximumFractionDigits: 4 });

export function Consignaciones({
  alFacturar,
}: {
  /** Abre una factura directa del cliente vendiendo desde la consignación. */
  alFacturar: (clienteId: string, ubicacionId: string, nombre: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [eligiendoCliente, setEligiendoCliente] = useState(false);
  const [mover, setMover] = useState<{ c: Consignacion; sentido: 'envios' | 'retornos' } | null>(
    null,
  );
  const [guia, setGuia] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'consignaciones'],
    queryFn: () =>
      api.get<{ items: Consignacion[] }>('/ventas/consignaciones', { query: { empresaId } }),
  });
  const abrir = useMutation({
    mutationFn: (clienteEntidadId: string) => {
      const cuerpo = { empresaId, clienteEntidadId };
      return api.post('/ventas/consignaciones', cuerpo, {
        idempotencia: intentos.clave('abrir-consignacion', cuerpo),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'consignaciones'] });
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'ubicaciones'] });
    },
    onError: (e) => intentos.cerrar(e),
  });
  const gestiona = puede('ventas.consignacion.gestionar');

  return (
    <div className="pantalla consignaciones" data-pantalla="consignaciones">
      <div className="sin-fuente" role="note">
        <b>CONSIGNACIÓN</b>
        <span>
          La mercadería sigue siendo de MADECI mientras está donde el cliente. Cada envío y cada
          retorno sale con su guía interna (GDI); lo consumido se factura con «Facturar lo
          consumido».
        </span>
      </div>
      <div className="barra-filtros">
        <span className="espaciador" />
        {gestiona && (
          <button type="button" className="primario" onClick={() => setEligiendoCliente(true)}>
            Abrir consignación (cliente)
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      {!!abrir.error && <PanelError error={abrir.error} />}
      {q.data && !q.data.items.length && (
        <p className="apagado">Todavía no hay consignaciones abiertas.</p>
      )}
      {(q.data?.items ?? []).map((c) => (
        <fieldset
          key={c.ubicacionId}
          className="recuadro-nc csg-recuadro"
          data-consignacion={c.codigo}
        >
          <legend>
            {c.cliente.razonSocial} {c.cliente.rut ? `· ${c.cliente.rut}` : ''} ·{' '}
            <span className="mono">{c.codigo}</span>
          </legend>
          <table className="ncd-lineas">
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th>Unid.</th>
                <th className="der">En consignación</th>
              </tr>
            </thead>
            <tbody>
              {c.stock.map((s) => (
                <tr key={s.productoId}>
                  <td className="mono">{s.sku}</td>
                  <td>{s.nombre}</td>
                  <td>{s.unidad}</td>
                  <td className="der mono">{cant(s.fisico)}</td>
                </tr>
              ))}
              {!c.stock.length && (
                <tr>
                  <td colSpan={4} className="apagado">
                    Sin mercadería en consignación.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="acciones csg-acciones">
            {gestiona && (
              <button type="button" onClick={() => setMover({ c, sentido: 'envios' })}>
                Enviar mercadería
              </button>
            )}
            {gestiona && c.stock.length > 0 && (
              <button type="button" onClick={() => setMover({ c, sentido: 'retornos' })}>
                Recibir lo que vuelve
              </button>
            )}
            {c.stock.length > 0 && puede('ventas.nota.crear') && (
              <button
                type="button"
                className="primario"
                onClick={() => alFacturar(c.cliente.id, c.ubicacionId, c.cliente.razonSocial)}
              >
                Facturar lo consumido
              </button>
            )}
          </div>
        </fieldset>
      ))}
      {eligiendoCliente && (
        <BuscadorCliente
          empresaId={empresaId}
          alElegir={(c) => {
            setEligiendoCliente(false);
            abrir.mutate(c.id);
          }}
          alCerrar={() => setEligiendoCliente(false)}
        />
      )}
      {mover && (
        <MoverConsignacion
          consignacion={mover.c}
          sentido={mover.sentido}
          alCerrar={() => setMover(null)}
          alTerminar={(guiaId) => {
            setMover(null);
            setGuia(guiaId);
          }}
        />
      )}
      {guia && <GuiaInternaImpresa guiaId={guia} alTerminar={() => setGuia(null)} />}
    </div>
  );
}

function MoverConsignacion({
  consignacion: c,
  sentido,
  alCerrar,
  alTerminar,
}: {
  consignacion: Consignacion;
  sentido: 'envios' | 'retornos';
  alCerrar: () => void;
  alTerminar: (guiaId: string) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const ubicaciones = useUbicaciones(empresaId);
  const bodegas = useMemo(
    () => (ubicaciones.data ?? []).filter((u) => u.tipo === 'BODEGA' || u.tipo === 'MESON'),
    [ubicaciones.data],
  );
  const envio = sentido === 'envios';
  const [bodegaId, setBodegaId] = useState('');
  const bodega = bodegaId || bodegas[0]?.id || '';
  const [lineas, setLineas] = useState<Linea[]>(() =>
    envio
      ? []
      : c.stock.map((s) => ({
          productoId: s.productoId,
          sku: s.sku,
          nombre: s.nombre,
          unidad: s.unidad,
          cantidad: '',
          tope: Number(s.disponible),
        })),
  );
  const [buscando, setBuscando] = useState(false);
  const [patente, setPatente] = useState('');
  const [chofer, setChofer] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const enviar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        bodegaId: bodega,
        lineas: lineas
          .filter((l) => Number(l.cantidad) > 0)
          .map((l) => ({ productoId: l.productoId, cantidadBase: String(Number(l.cantidad)) })),
        ...(patente.trim() ? { patente: patente.trim() } : {}),
        ...(chofer.trim() ? { chofer: chofer.trim() } : {}),
        ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
      };
      return api.post<{ guia: { guiaId: string } }>(
        `/ventas/consignaciones/${c.ubicacionId}/${sentido}`,
        cuerpo,
        { idempotencia: intentos.clave(`consignacion-${sentido}`, cuerpo) },
      );
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'consignaciones'] });
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'disponibilidad'] });
      alTerminar(r.guia.guiaId);
    },
    onError: (e) => intentos.cerrar(e),
  });
  const problemas: string[] = [];
  if (!bodega) problemas.push('Elige la bodega.');
  if (!lineas.some((l) => Number(l.cantidad) > 0)) problemas.push('Indica alguna cantidad.');
  for (const l of lineas)
    if (l.tope !== undefined && Number(l.cantidad) > l.tope)
      problemas.push(
        `${l.sku}: vuelve más de lo que hay en consignación (${cant(String(l.tope))}).`,
      );

  return (
    <Modal
      titulo={
        envio
          ? `Enviar a consignación · ${c.cliente.razonSocial}`
          : `Retorno de consignación · ${c.cliente.razonSocial}`
      }
      ancho="ancho"
      alCerrar={() => !enviar.isPending && alCerrar()}
      pie={
        <>
          <button type="button" onClick={alCerrar} disabled={enviar.isPending}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            disabled={!!problemas.length || enviar.isPending}
            onClick={() => enviar.mutate()}
          >
            {enviar.isPending ? 'Procesando…' : 'Confirmar e imprimir guía'}
          </button>
        </>
      }
    >
      <div className="fila-campos">
        <label className="campo">
          <span className="etiqueta">{envio ? 'Sale de la bodega' : 'Vuelve a la bodega'}</span>
          <select aria-label="Bodega" value={bodega} onChange={(e) => setBodegaId(e.target.value)}>
            {bodegas.map((u) => (
              <option key={u.id} value={u.id}>
                {u.codigo} · {u.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Patente</span>
          <input
            aria-label="Patente"
            maxLength={12}
            value={patente}
            onChange={(e) => setPatente(e.target.value.toUpperCase())}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Chofer</span>
          <input
            aria-label="Chofer"
            maxLength={80}
            value={chofer}
            onChange={(e) => setChofer(e.target.value)}
          />
        </label>
      </div>
      <table className="ncd-lineas">
        <thead>
          <tr>
            <th>Código</th>
            <th>Producto</th>
            <th>Unid.</th>
            {!envio && <th className="der">En consignación</th>}
            <th className="der">Cantidad (unid. base)</th>
            {envio && <th />}
          </tr>
        </thead>
        <tbody>
          {lineas.map((l, i) => (
            <tr key={l.productoId}>
              <td className="mono">{l.sku}</td>
              <td>{l.nombre}</td>
              <td>{l.unidad}</td>
              {!envio && <td className="der mono">{cant(String(l.tope ?? 0))}</td>}
              <td className="der">
                <input
                  className="ncd-valor"
                  aria-label={`Cantidad ${l.sku}`}
                  inputMode="decimal"
                  value={l.cantidad}
                  onChange={(e) =>
                    setLineas((ls) =>
                      ls.map((x, k) =>
                        k === i
                          ? {
                              ...x,
                              cantidad: e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'),
                            }
                          : x,
                      ),
                    )
                  }
                />
              </td>
              {envio && (
                <td>
                  <button
                    type="button"
                    className="boton-mini"
                    aria-label={`Quitar ${l.sku}`}
                    onClick={() => setLineas((ls) => ls.filter((_, k) => k !== i))}
                  >
                    ✕
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {envio && (
        <button type="button" className="ncd-todo" onClick={() => setBuscando(true)}>
          Agregar producto (F3)
        </button>
      )}
      {!envio && (
        <button
          type="button"
          className="ncd-todo"
          onClick={() =>
            setLineas((ls) => ls.map((l) => ({ ...l, cantidad: String(l.tope ?? 0) })))
          }
        >
          Vuelve todo
        </button>
      )}
      <label className="campo">
        <span className="etiqueta">Observaciones</span>
        <input
          aria-label="Observaciones"
          maxLength={500}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </label>
      {problemas.length > 0 && (
        <ul className="ncd-problemas">
          {problemas.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {!!enviar.error && <PanelError error={enviar.error} />}
      {buscando && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={bodega}
          ubicaciones={ubicaciones.data ?? []}
          alElegir={(e) => {
            setBuscando(false);
            setLineas((ls) =>
              ls.some((l) => l.productoId === e.producto.id)
                ? ls
                : [
                    ...ls,
                    {
                      productoId: e.producto.id,
                      sku: e.producto.sku,
                      nombre: e.producto.nombre,
                      unidad: e.producto.unidad_base_codigo,
                      cantidad: '',
                    },
                  ],
            );
          }}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </Modal>
  );
}
